// @flow
// Page transitions in the phone layout, like a native app: going deeper pushes the new page in from the right,
// going back pops it off, the patient's tabs crossfade, and the footer's tabs switch instantly (as tab bars do).
//
// The direction comes mostly from where you go: the header's back arrow replaces the route with its parent, and
// some back buttons push the page they came from. A return to the page before (Android's back button, a POP, or
// such a push) pops.
//
// It plays with the View Transitions API: the browser keeps a snapshot of the old page while React renders the
// new one, so nothing is mounted twice. The CSS is in layouts/PageTransitions.scss. WebViews without the API (iOS
// before 18, Android WebView before 111), reduced motion and the desktop layout swap pages at once, as before.
import ReactDOM from 'react-dom';
import { finishExits } from './sheetMotion';

export type NavDirection = 'forward' | 'back' | 'tab' | 'none';

// The footer's tabs (AppMobileFooter)
const TAB_ROOTS = ['/agenda', '/pacientes/lista', '/relatorios', '/ajustes'];

const sectionOf = (path: string) => {
  if (/^\/agenda(\/|$)/.test(path)) return 'agenda';
  if (/^\/pacientes?(\/|$)/.test(path)) return 'patients';
  if (/^\/relatorios(\/|$)/.test(path)) return 'reports';
  if (/^\/ajustes(\/|$)/.test(path)) return 'settings';
  return null;
};

const depth = (path: string) => path.split('/').filter(Boolean).length;

const isAncestor = (a: string, b: string) => b.startsWith(a.endsWith('/') ? a : `${a}/`);

// The patient's tabs: Cadastro, Prontuário, Financeiro (PatientMobileHeader)
const PATIENT_PAGE = /^\/paciente\/(\d+)\/([^/]+)/;
const PATIENT_TAB = /^\/paciente\/(\d+)\/(cadastro|prontuario|financeiro)$/;

// How the page changes from `prev` to `next`, by where each sits in the app
export const navDirection = (prev: ?string, next: string): NavDirection => {
  if (!prev || prev === next) return 'none';
  const a = sectionOf(prev);
  const b = sectionOf(next);
  if (!a || !b) return 'none'; // the start, login, plans, not found…

  const from = PATIENT_PAGE.exec(prev);
  const tab = PATIENT_TAB.exec(next);
  if (from && tab && from[1] === tab[1] && from[2] !== tab[2]) return 'tab';

  if (isAncestor(next, prev)) return 'back';
  if (isAncestor(prev, next)) return 'forward';

  if (TAB_ROOTS.includes(next)) return a === b ? 'back' : 'none'; // a footer tab, or up to its root
  if (a === b) {
    if (depth(next) === depth(prev)) return 'none';
    return depth(next) > depth(prev) ? 'forward' : 'back';
  }
  return 'forward'; // into another section's page: the agenda opening a patient
};

// Follows the pages visited (as the history does: PUSH adds, REPLACE swaps, POP goes back), so a return to the
// page before pops even where the hierarchy alone wouldn't say so (the agenda → a patient → Android's back)
export const createNavTracker = () => {
  let stack: Array<string> = [];
  return (path: string, action: string): NavDirection => {
    const prev = stack[stack.length - 1];
    if (prev === path) return 'none';
    if (action === 'REPLACE' && stack.length) {
      stack[stack.length - 1] = path;
      return navDirection(prev, path);
    }
    if (stack.length > 1 && stack[stack.length - 2] === path) {
      if (action === 'POP') stack.pop();
      else stack = [...stack.slice(-49), path];
      const came = navDirection(path, prev);
      if (came === 'forward') return 'back';
      if (came === 'tab') return 'tab';
      return navDirection(prev, path);
    }
    stack = [...stack.slice(-49), path];
    return navDirection(prev, path);
  };
};

const prefersReducedMotion = () => (
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

let playing = 0;
// The render of a transition still waiting for the browser: any later page change renders it first, so the
// screen never goes back to an older page
let pending: ?Function = null;

// Something on top of the page would move with it, under the header and the footer: no transition then. A modal
// or a dialog, the appointment's form (DoubleModal), the date picker, the patient's side panels on the phone.
const OVERLAYS = [
  '.modal:not(.motionExit)', '.swal2-container', '.doubleModal', '.dayPickerOverlay',
  '#mobileApp .modalSummary.visible', '#patientWidgets.mobile.visible',
].join(', ');
const overlayOpen = () => Boolean(document.querySelector(OVERLAYS));

// Runs `update` (the router's re-render) inside a view transition that plays `direction`, or right away
export const runPageTransition = (direction: NavDirection, update: Function) => {
  if (pending) pending();
  const doc: any = document;
  if (
    direction === 'none' || typeof doc.startViewTransition !== 'function' || !doc.getElementById('mobileApp') ||
    prefersReducedMotion() || doc.visibilityState === 'hidden' || overlayOpen()
  ) {
    update();
    return;
  }

  // A sheet still sliding out (sheetMotion's copy) would be caught in both pages: it goes now
  finishExits();
  let done = false;
  let fallback = null;
  const render = () => {
    if (done) return;
    done = true;
    clearTimeout(fallback);
    if (pending === render) pending = null;
    ReactDOM.flushSync(update);
  };
  pending = render;
  const html = doc.documentElement;
  playing += 1;
  const token = playing;
  html.setAttribute('data-nav', direction);
  const end = () => { if (playing === token) html.removeAttribute('data-nav'); };
  try {
    const transition = doc.startViewTransition(render);
    // A skipped transition (another navigation, two elements with the same name) still renders the page
    transition.ready.catch(() => {});
    transition.finished.then(end, end);
    // The page must change even if the browser never gets to the update: drop the animation, then render
    fallback = setTimeout(() => {
      if (done) return;
      try { transition.skipTransition(); } catch (e) { /* already over */ }
      render();
    }, 1000);
  } catch (e) {
    end();
    render();
  }
};

// Wraps the history so each page change plays its transition: one per change, whatever the number of listeners,
// all of them rendered inside it. A change that keeps the location (the active footer tab tapped again) plays none.
export const withPageTransitions = (history: Object) => {
  const track = createNavTracker();
  track(history.location.pathname, 'POP');
  const listeners: Array<Function> = [];
  let last = history.location;
  history.listen((location, action) => {
    const direction = location === last ? 'none' : track(location.pathname, action);
    last = location;
    runPageTransition(direction, () => listeners.slice().forEach((listener) => listener(location, action)));
  });
  // eslint-disable-next-line no-param-reassign
  history.listen = (listener: Function) => {
    listeners.push(listener);
    return () => {
      const i = listeners.indexOf(listener);
      if (i >= 0) listeners.splice(i, 1);
    };
  };
  return history;
};
